import { useEffect, useRef, useState } from 'react'
import { ArrowUp, Check, Mic, Plus, Square, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export type AspectRatio = '16:9' | '9:16' | '1:1' | '4:5' | '4:3'

export const ASPECT_RATIOS: { id: AspectRatio; label: string; tagline: string; w: number; h: number }[] = [
  { id: '16:9', label: '16:9', tagline: 'YouTube, Widescreen', w: 18, h: 11 },
  { id: '9:16', label: '9:16', tagline: 'TikTok, Reels, Shorts', w: 11, h: 18 },
  { id: '1:1',  label: '1:1',  tagline: 'Instagram, LinkedIn', w: 14, h: 14 },
  { id: '4:5',  label: '4:5',  tagline: 'Instagram, Facebook Ads', w: 12, h: 15 },
  { id: '4:3',  label: '4:3',  tagline: 'Classic, Presentations', w: 16, h: 12 },
]

function AspectRatioGlyph({ w, h, color = 'currentColor' }: { w: number; h: number; color?: string }) {
  return (
    <span
      className="inline-block rounded-[3px] border-[1.5px] shrink-0"
      style={{ width: w, height: h, borderColor: color }}
    />
  )
}

export function WelcomeBlock() {
  return (
    <div className="text-center">
      <h2 className="text-[28px] md:text-[40px] leading-[1.1] font-normal text-foreground tracking-tight pt-[20px] pb-[5px]">
        What are we making today?
      </h2>
    </div>
  )
}

export function Composer({
  value,
  onChange,
  onSend,
  isStreaming = false,
  onCancel,
  aspectRatio,
  onAspectRatioChange,
  onAttachClick,
}: {
  value: string
  onChange: (v: string) => void
  onSend: (files?: File[]) => void
  isStreaming?: boolean
  onCancel?: () => void
  aspectRatio?: AspectRatio
  onAspectRatioChange?: (ar: AspectRatio) => void
  /**
   * Intercept any attempt to open the file picker (via the + button or the
   * `/` shortcut). Return `true` to suppress the default picker — useful for
   * gating uploads behind auth.
   */
  onAttachClick?: () => boolean
}) {
  const [files, setFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [dictating, setDictating] = useState(false)
  const recognitionRef = useRef<any>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  useEffect(() => {
    if (!settingsOpen) return
    const onClick = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [settingsOpen])
  // Snapshot of textarea contents at the moment dictation started, so live
  // partial transcripts can be appended without overwriting earlier typing.
  const dictateBaseRef = useRef('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const onTextField = document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT'
      if (e.key === '/' && !onTextField) {
        e.preventDefault()
        if (onAttachClick?.()) return
        fileInputRef.current?.click()
      }
      // Ctrl+D toggles dictation (matches the tooltip hint)
      if (e.key.toLowerCase() === 'd' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        toggleDictation()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAttachClick])

  // Stop recognition on unmount.
  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop() } catch { /* noop */ }
    }
  }, [])

  const startDictation = () => {
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) {
      alert('Voice dictation isn\'t supported in this browser. Try Chrome or Safari.')
      return
    }
    const r = new SR()
    r.continuous = true
    r.interimResults = true
    r.lang = navigator.language || 'en-US'
    dictateBaseRef.current = value
    r.onresult = (event: any) => {
      let transcript = ''
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript
      }
      const base = dictateBaseRef.current
      const sep = base && !/\s$/.test(base) ? ' ' : ''
      onChange(base + sep + transcript)
    }
    r.onend = () => setDictating(false)
    r.onerror = () => setDictating(false)
    recognitionRef.current = r
    try {
      r.start()
      setDictating(true)
    } catch {
      setDictating(false)
    }
  }

  const stopDictation = () => {
    try { recognitionRef.current?.stop() } catch { /* noop */ }
    setDictating(false)
  }

  const toggleDictation = () => {
    if (dictating) stopDictation()
    else startDictation()
  }

  const handleFiles = (list: FileList | null) => {
    if (!list || !list.length) return
    setFiles((prev) => [...prev, ...Array.from(list)])
  }

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  const submit = () => {
    if (dictating) stopDictation()
    onSend(files.length ? files : undefined)
    setFiles([])
  }

  const canSend = !isStreaming && (value.trim().length > 0 || files.length > 0)

  return (
    <div className="relative">
      <div className="rounded-[28px] bg-white border border-zinc-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.08)] px-5 pt-5 pb-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = '' }}
        />

        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-full bg-zinc-100 text-[12px] text-zinc-700 max-w-[200px]">
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="w-5 h-5 rounded-full flex items-center justify-center text-zinc-500 hover:bg-zinc-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder={dictating ? 'Listening… speak now' : 'Describe your video…'}
          rows={1}
          className="w-full resize-none border-0 outline-none bg-transparent text-[16px] text-zinc-900 placeholder:text-zinc-400 leading-relaxed overflow-y-auto"
          style={{ minHeight: '28px', maxHeight: '200px' }}
        />
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1.5">
            <div className="relative group">
              <button
                type="button"
                onClick={() => {
                  if (onAttachClick?.()) return
                  fileInputRef.current?.click()
                }}
                className="w-9 h-9 rounded-full flex items-center justify-center text-zinc-700 bg-zinc-100 hover:bg-zinc-200"
              >
                <Plus className="h-[18px] w-[18px]" />
              </button>
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-zinc-900 text-white text-[12px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                Add files and more
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-zinc-700 text-zinc-200 text-[10px]">/</span>
              </div>
            </div>
            {onAspectRatioChange && (() => {
              const activeRatio = ASPECT_RATIOS.find((r) => r.id === aspectRatio) ?? ASPECT_RATIOS[0]
              return (
              <div ref={settingsRef} className="relative">
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setSettingsOpen((o) => !o)}
                    className={cn(
                      'w-9 h-9 rounded-full flex items-center justify-center transition-colors',
                      settingsOpen
                        ? 'bg-zinc-900 text-white'
                        : 'text-zinc-700 hover:bg-zinc-100',
                    )}
                  >
                    <AspectRatioGlyph w={activeRatio.w} h={activeRatio.h} color="currentColor" />
                  </button>
                  {!settingsOpen && (
                    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 inline-flex items-center h-6 px-2 rounded-md bg-zinc-900 text-white text-[12px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                      Change aspect ratio
                    </div>
                  )}
                </div>
                {settingsOpen && (
                  <div className="absolute left-0 bottom-full mb-2 w-[260px] rounded-2xl bg-white border border-zinc-200/80 shadow-[0_8px_32px_-4px_rgba(0,0,0,0.12)] p-1.5 z-30">
                    <div className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
                      Aspect ratio
                    </div>
                    {ASPECT_RATIOS.map((r) => {
                      const selected = r.id === aspectRatio
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            onAspectRatioChange(r.id)
                            setSettingsOpen(false)
                          }}
                          className={cn(
                            'w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors',
                            selected ? 'bg-sky-50' : 'hover:bg-zinc-50',
                          )}
                        >
                          <span className="w-7 flex items-center justify-center shrink-0">
                            <AspectRatioGlyph w={r.w} h={r.h} color={selected ? '#0ea5e9' : '#a1a1aa'} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className={cn('text-[13px] font-semibold', selected ? 'text-sky-600' : 'text-zinc-900')}>{r.label}</div>
                            <div className="text-[11px] text-zinc-500">{r.tagline}</div>
                          </div>
                          {selected && <Check className="h-4 w-4 text-sky-600 shrink-0" />}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              )
            })()}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative group">
              <button
                type="button"
                onClick={toggleDictation}
                className={cn(
                  'w-9 h-9 rounded-full flex items-center justify-center transition-colors',
                  dictating
                    ? 'bg-rose-500 text-white animate-pulse'
                    : 'text-zinc-700 hover:bg-zinc-100',
                )}
              >
                <Mic className="h-[18px] w-[18px]" />
              </button>
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-zinc-900 text-white text-[12px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                {dictating ? 'Stop dictation' : 'Speak to the agent'}
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-zinc-700 text-zinc-200 text-[10px]">⌃D</span>
              </div>
            </div>
            {isStreaming && onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                title="Stop"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-rose-500/10 text-rose-600 hover:bg-rose-500/15 transition-colors"
              >
                <Square className="h-[14px] w-[14px]" fill="currentColor" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canSend}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors',
                  canSend ? 'bg-zinc-900 hover:bg-zinc-800' : 'bg-zinc-300',
                )}
              >
                <ArrowUp className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
